import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { Textarea } from "@chakra-ui/react";
import StandardModal from "../shared/StandardModalV2";
import iconFeedback from "../../../images/IconFeedback.svg?url";
import iconFeedbackSuccess from "../../../images/IconFeedbackSuccess.svg?url";
import { submitFeedback } from "../../api/feedbackApi";
import { Box, Button, InlineNotification, Text } from "@familyzone/component-library";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";

interface Props {
	handleClose: () => void;
	classroomName: string;
}

const FeedbackModal: React.FC<Props> = ({ classroomName, handleClose }) => {
	const [currentMessage, setCurrentMessage] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>("");
	const selectedClassroom = useSelectedClassroomStore();

	const handleMessageChange = (evt: { target: { value: string } }) => {
		setCurrentMessage(evt.target.value);
	};

	const handleSubmit = () => {
		if (currentMessage === "") {
			setErrorMessage("Please enter a message.");
			return;
		}

		if (!classroomName || !selectedClassroom.stableIdV1 || !selectedClassroom.id) {
			setErrorMessage("Error occured when processing, please try again later.");
			return;
		}

		setIsSubmitting(true);
		setErrorMessage("");

		submitFeedback(currentMessage, classroomName, selectedClassroom.stableIdV1, selectedClassroom.id, {}).then(
			() => {
				setIsSubmitting(false);
				setHasSubmitted(true);
			},
			() => {
				setIsSubmitting(false);
				setErrorMessage("There was a problem submitting your feedback, please email insights@familyzone.com.");
			}
		);
	};

	return (
		<StandardModal open close={handleClose} size="medium" contentClassName="FeedbackModal">
			<img src={hasSubmitted ? iconFeedbackSuccess : iconFeedback} alt="" />
			<h2>{hasSubmitted ? "Feedback sent!" : "Give us your feedback about Classwize"}</h2>
			{hasSubmitted ? (
				<p>Thank you for your feedback. We&apos;ll make sure the right people see it!</p>
			) : (
				<Fragment>
					<Box mb="sp16">
						<Text fontFamily="paragraphs" fontSize="16px" color="text.paragraph.regular">
							Feedback from educators like you helps us improve Classwize for everyone. We would love to know your feedback and new ideas.
						</Text>
					</Box>
					<InlineNotification
						status="info"
						notificationDescription={
							<Text data-testid="FeedbackModalSupportMessage" textAlign="left">
								This form is intended to receive product feedback and it will <b>not</b> initiate technical support. If you are experiencing
								technical difficulties with Classwize, please reach out to your school&apos;s IT support staff so they can work with our
								engineers on finding a solution.
							</Text>
						}
					/>

					<Textarea
						data-testid="FeedbackModalTextArea"
						sx={{ height: "200px", mh: "50px" }}
						className="FeedbackModal_textArea"
						name="messageBody"
						onChange={handleMessageChange}
						value={currentMessage}
					/>
					<p className="error">{errorMessage}</p>
					<Button variant="primary" onClick={handleSubmit} isDisabled={isSubmitting} data-testid="submit-feedback-button">
						Submit Feedback
					</Button>
				</Fragment>
			)}
		</StandardModal>
	);
};

FeedbackModal.propTypes = {
	handleClose: PropTypes.func.isRequired,
};

export default FeedbackModal;
